export const ENV: "testnet" | "mainnet" | "private-mainnet" =
  import.meta.env.VITE_ENV || "testnet";

export const EXPLORER_BASE_URL: string = (function () {
  if (ENV == "testnet") {
    return "https://testnet.purrsec.com";
  }
  return "https://hyperevmscan.io";
})();

export const CORE_EXPLORER_BASE_URL = (function () {
  if (ENV == "testnet") {
    return "https://testnet.hypurrscan.io";
  }
  return "https://hypurrscan.io";
})();

export const APP_URL = (function () {
  if (ENV == "testnet") {
    return "https://testnet.helmfinance.xyz";
  }
  return "https://app.helmfinance.xyz";
})();

export const HYPERLIQUID_APP_URL = (function () {
  if (ENV == "testnet") {
    return "https://app.hyperliquid-testnet.xyz";
  }
  return "https://app.hyperliquid.xyz";
})();

export const HYPERLIQUID_API_URL = (function () {
  if (ENV == "testnet") {
    return "https://api.hyperliquid-testnet.xyz/info";
  }
  return "https://api.hyperliquid.xyz/info";
})();

const testnetEnv = {
  swapRouter: "0xb812979D16bD00C573C376BDBbC8d1aC55E9793D".toLowerCase(),
  lensAddress: "0xEF3f53c38A5BED844CF32b10fd590586F8D63AC5".toLowerCase(),
  tokens: {
    WHYPE: "0x5555555555555555555555555555555555555555".toLowerCase(),
    UETH: "0x5a1A1339ad9e52B7a4dF78452D5c18e8690746f3".toLowerCase(),
    USDC: "0x2b3370ee501b4a559b57d449569354196457d8ab".toLowerCase(),
    USDH: "0x22222245c52c817f95b74664ae8546b490222222".toLowerCase(),
  },
  addressToSpotIndex: {
    ["0x5a1A1339ad9e52B7a4dF78452D5c18e8690746f3".toLowerCase()]: "@151", // UETH
    ["0x5555555555555555555555555555555555555555".toLowerCase()]: "@107", // WHYPE/HYPE
    ["0x22222245c52c817f95b74664ae8546b490222222".toLowerCase()]: "@230", // USDH
  },
} as const;

const privateMainnetEnv = {
  swapRouter: "0x18ebC3F95CD6B5db55A15864079019C5d2b83DBC".toLowerCase(),
  lensAddress: "0xa099e8a1B9646e4B56b09644D0672d87BA3a0b60".toLowerCase(),
  tokens: {
    WHYPE: "0x5555555555555555555555555555555555555555".toLowerCase(),
    UETH: "0xbe6727b535545c67d5caa73dea54865b92cf7907".toLowerCase(),
    UBTC: "0x9fdbda0a5e284c32744d2f17ee5c74b284993463".toLowerCase(),
    USDC: "0xb88339CB7199b77E23DB6E890353E22632Ba630f".toLowerCase(),
    USDH: "0x111111a1a0667d36bd57c0a9f569b98057111111".toLowerCase(),
  },
  addressToSpotIndex: {
    ["0xbe6727b535545c67d5caa73dea54865b92cf7907".toLowerCase()]: "@151", // UETH
    ["0x9fdbda0a5e284c32744d2f17ee5c74b284993463".toLowerCase()]: "@142", // UBTC
    ["0x5555555555555555555555555555555555555555".toLowerCase()]: "@107", // WHYPE/HYPE
    ["0x111111a1a0667d36bd57c0a9f569b98057111111".toLowerCase()]: "@230", // USDH
  },
};

export const envAddresses = (function () {
  if (ENV == "testnet") {
    return testnetEnv;
  }

  if (ENV == "private-mainnet") {
    return privateMainnetEnv;
  }

  // fallback now.
  return privateMainnetEnv;
})();

// Keeper bot wallet addresses (from docs/infra/kms_wallets.md)
export interface KeeperBot {
  name: string;
  address: `0x${string}`;
}

const testnetKeepers: KeeperBot[] = [
  { name: "Daemon", address: "0xa942bdebeb1c8cf08b4f438eeae7958ec066f0a0" },
  { name: "Pauser", address: "0x8c18ed5deb3977128c5262fc961729205f5a9d26" },
  {
    name: "MI Factor Updater",
    address: "0x53c9f943fc160e5774be7bff45b270e87596c184",
  },
  {
    name: "Execution Fee Updater",
    address: "0xfcaf4cd0d519f2d3d59bf31a362bb5a28f5c29f6",
  },
  {
    name: "Pyth Price Pusher",
    address: "0x676767f3817da6520d1ea00f1c6e9b906a489f07",
  },
  {
    name: "Staking Operator",
    address: "0x07994043a60aefb8943928193dbf855b2387f9ed",
  },
];

const privateMainnetKeepers: KeeperBot[] = [
  { name: "Daemon", address: "0xfe387dab8ed44a7a0229ecee045fa32a283ec8e1" },
  { name: "Pauser", address: "0x548bb2c813951325066fdadec7879e79ce6b024b" },
  {
    name: "MI Factor Updater",
    address: "0xd65438798c9db53c4dd0dfb0c90cfc15e7ac80e7",
  },
  {
    name: "Execution Fee Updater",
    address: "0x6eb890a85054e6d87ea59f438246e0fa98405956",
  },
  {
    name: "Pyth Price Pusher",
    address: "0xeb0732a1554aaf22f29a59f2179f867f7d031b43",
  },
  {
    name: "Staking Operator",
    address: "0x55a2b63906336318cc3a74c10af8f4e12204c415",
  },
];

export const keeperBots: KeeperBot[] = (function () {
  if (ENV == "testnet") return testnetKeepers;
  return privateMainnetKeepers;
})();

// GraphQL Configuration
export const GRAPHQL_ENDPOINT = import.meta.env.VITE_GRAPHQL_ENDPOINT;
export const ANALYSIS_GRAPHQL_ENDPOINT = import.meta.env
  .VITE_ANALYSIS_GRAPHQL_ENDPOINT as string | undefined;

// Validate that the endpoint is configured
if (!GRAPHQL_ENDPOINT) {
  console.warn(
    "VITE_GRAPHQL_ENDPOINT is not configured. Please set it in your .env file.",
  );
}

export const USE_MOCK =
  String(import.meta.env.VITE_SUBGRAPH_MOCK || "").trim() === "1" ||
  String(import.meta.env.VITE_SUBGRAPH_MOCK || "").toLowerCase() === "true";

// API Configuration
export const API_BASE_URL =
  import.meta.env.VITE_API_URL || "http://localhost:3001";

/**
 * Whether access code is required for registration (uses API auth with SIWE)
 */
export const REQUIRE_ACCESS_CODE =
  import.meta.env.VITE_REQUIRE_ACCESS_CODE === "true";

/**
 * Whether access control is enabled (uses client-side signature verification)
 */
export const ENABLE_ACCESS_CONTROL =
  import.meta.env.VITE_ENABLE_ACCESS_CONTROL === "true";

/**
 * Enable risk dashboard (internal tool, protected by Vercel auth)
 */
export const ENABLE_RISK_DASHBOARD =
  import.meta.env.VITE_ENABLE_RISK_DASHBOARD === "true";

export const ENABLE_POINTS_REFERRAL =
  import.meta.env.VITE_ENABLE_POINTS_REFERRAL === "true";

export const ENABLE_POOL_STAKING =
  import.meta.env.VITE_ENABLE_POOL_STAKING === "true";

/**
 * Status colors for risk indicators (leverage, delta, funding rate, positions)
 */
export const STATUS_COLORS = {
  GREEN: "#22c55e",
  YELLOW: "#eab308",
  RED: "#ef4444",
} as const;

/**
 * Display decimals per token when showing amounts in the UI
 */
export const DISPLAY_DECIMALS: Record<string, number> = {
  WHYPE: 4,
  UETH: 6,
  UBTC: 8,
  USDC: 4,
  USDH: 4,
};

/**
 * Token symbols whose Core ↔ EVM ERC20 linkage is trusted by the team.
 * The bridge form suppresses the "verify the destination contract" warning
 * for these. Tokens NOT in this set surface that warning so users can sanity-
 * check the linked EVM contract before sending — an incorrectly-linked contract
 * on the EVM side can cause loss of funds after an otherwise-successful Core
 * transfer.
 */
export const VERIFIED_BRIDGE_TOKENS = new Set<string>([
  "USDC",
  "USDH",
  "USDT0",
  "USDE",
  "HYPE",
  "HPL",
  "UETH",
  "UBTC",
  "USOL",
  "UXPL",
  "UMON",
  "UFART",
  "UPUMP",
  "UBONK",
  "USPX",
  "U2Z",
  "UENA",
  "NKTQ",
]);
